import { useState, useEffect, useCallback, useRef } from 'react';
import { UnauthorizedError } from './api';
import { useApp } from '../AppContext';

/**
 * Load data for a screen. Returns { data, error, loading, reload, setData }.
 *
 * Guards against the two bugs this pattern usually grows: setting state after
 * the component unmounted, and a slow earlier request overwriting a faster
 * later one when deps change mid-flight.
 */
export function useAsync(fn, deps = []) {
  const [state, setState] = useState({ data: undefined, error: null, loading: true });
  const { signOut } = useApp();
  const seq = useRef(0);
  const alive = useRef(true);

  // Must set alive back to true on mount, not just false on unmount. React
  // StrictMode mounts, unmounts and remounts every component — and any route
  // the user navigates away from and back to does the same. Only clearing the
  // flag left it false for the rest of the component's life, so every
  // subsequent setState was suppressed and the screen sat on "Loading…"
  // forever even though the request had returned 200.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await fn();
      if (alive.current && mine === seq.current) setState({ data, error: null, loading: false });
    } catch (e) {
      if (e instanceof UnauthorizedError) { signOut(); return; }
      if (alive.current && mine === seq.current) {
        setState({ data: undefined, error: e.message || 'Failed to load', loading: false });
      }
    }
    // fn is recreated every render by design; deps drive reloading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { load(); }, [load]);

  const setData = useCallback((updater) => {
    setState(s => ({ ...s, data: typeof updater === 'function' ? updater(s.data) : updater }));
  }, []);

  return { ...state, reload: load, setData };
}
